import './walk-in-component';
import CameraFramingHelper from '../../view3d/camera-framing-helper';
import HotkeyManager, { KeyMapping } from '../../managers/hotkey-manager';
import ReadySignal from '../../control-flow/ready-signal';
import RenderLoop from '../../control-flow/render-loop';
import RoomView from '../../views/room-view';
import UpdateSceneEnvironment from '../../view3d/update-scene-environment';
import WalkInComponent from './walk-in-component';
import exportForRender from '../../view3d/export-for-render';
import getLogger from '../../logging';
import makeStandardRenderer from '../../view3d/make-standard-renderer';
import mfac from '../../material-factory';
import sizeReady from '../../html/size-ready';
import { Color, DefaultLoadingManager, PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import { Material, Room } from '../../domain';
import { RoomViewConfig, roomViewDefaults, SavedViewConfig, savedViewDefaults, ShowWallConfig, showWallDefaults } from '../../views/views-config';
import { WalkInCameraManager } from './walk-in-camera-manager';
import { applySavedViewCamera, extractSavedViewCamera } from '../../controllers/saved-view-controllers';
import { assign, clone, OptionalProperties, update } from "../../json/optional-properties";
import { focalLengthToVFov } from '../../view3d/focal-length-to-vfov';
import { optimize as optimizeGlobalLightingView } from '../../views/directional-lighting-view';
import { overrideMaterialFactory } from '../../cps';
import { registerListener } from '../../html/register-listener';
import { saveBuffer } from '../../utils/save-buffer';
import { setupResizeHandler } from '../../view3d/setup-resize-handler';

const log = getLogger('walk-in');

export type WalkInAppConfig = {
    savedViews: SavedViewConfig[],
    room: RoomViewConfig,
    showWalls: ShowWallConfig[],
    environmentAssetId: string,
};
export type WalkInAppOptions = OptionalProperties<WalkInAppConfig>;

const walkInAppDefaults: WalkInAppConfig = {
    savedViews: [clone(savedViewDefaults)],
    room: update(
        clone(roomViewDefaults), 
        {
            tempIncludeFloor: true,
            tempIncludeGlobalLighting: true,
            wallMaterial: {
                assetId: "materials/wall-paint",
            },
        }
    ),
    showWalls: [],
    environmentAssetId: "environments/studio-01",
};

const hotkeyMappings: KeyMapping[] = [
    { combo: "f", action: "toggle-render-frame-visibility", global: false},
    { combo: "o", action: "toggle-overhead-view", global: false},
];

export const init = (room: Room, options: WalkInAppOptions): WalkInApp => {
    return new WalkInApp(room, options);
}

export class WalkInApp {
    config: WalkInAppConfig = clone(walkInAppDefaults);

    room: Room;
    view: RoomView;
    root: WalkInComponent;

    scene: Scene = new Scene();
    // the field of view given here will be overridden by the framing helper
    camera: PerspectiveCamera = new PerspectiveCamera(80, 1, 1, 3000);
    framingHelper: CameraFramingHelper = new CameraFramingHelper(
        this.camera, focalLengthToVFov(22), 16/9);
    renderer: WebGLRenderer;
    sceneEnvironment: UpdateSceneEnvironment;
    loop: RenderLoop;

    onReady = new ReadySignal('WalkInApp');

    nav: WalkInCameraManager = new WalkInCameraManager(this.camera);

    constructor(room: Room, options: WalkInAppOptions) {
        overrideMaterialFactory();
        this.config = update(this.config, options);
        this.room = room;
        this.view = new RoomView(room, this.config.room);
        // TODO: include this after unit-editor merged into development
        //this.onReady.depends(this.view.onReady);
        optimizeGlobalLightingView(this.room);

        for(const wall of room.walls) {
            this.config.showWalls.push({
                wallOrder: wall.wallOrder,
                show: true,
                island: wall.island
            });
        }

        this.root = new WalkInComponent(
            this.config.room.directionalLighting, this.config.savedViews, this.config.showWalls);

        this.framingHelper.visible = false;

        this.renderer = makeStandardRenderer(this.root.canvas);
        this.loop = new RenderLoop(this.render);
        this.loop.onBeforeRender.add(this.view.updateFunctionBasedViews);

        setupResizeHandler(
            this.root.canvas, this.camera, this.renderer, this.resize);

        mfac.onLoad.add(this.loop.update);

        registerListener(this.root, 'wall-visibility-change', this.showWallConfigChanged);
        registerListener(this.root, 'nav-mode-change', () => {
            this.nav.switchMode(this.root.cameraPanel.getMode());
        });
        this.nav.onModeSwitch.add(() => this.root.cameraPanel.setMode(this.nav.mode));

        registerListener(this.root, 'update-current-view-config', this.updateCurrentViewConfig);
        registerListener(this.root, 'apply-current-view-config', this.applyCurrentViewConfig);
        registerListener(this.root, 'global-lighting-input', this.directionalLightingInput);
        registerListener(this.root, 'global-lighting-change', this.directionalLightingChange);
        registerListener(this.root, 'wall-color-input', this.showTemporaryWallColor);
        registerListener(this.root, 'wall-color-change', this.updateWallColor);
        registerListener(this.root,
                         'render-frame-visibility-change', this.updateRenderFrameVisibility);
        registerListener(this.root,
                         'camera-focal-length-input', this.updateCameraFocalLength);
        registerListener(this.root,
                         'camera-focal-length-change', this.updateCameraFocalLength);

        const hotkeys = HotkeyManager.getInstance();
        hotkeys.addMappings(hotkeyMappings);
        hotkeys.on('toggle-render-frame-visibility', this.toggleRenderFrameVisibility);

        this.scene = new Scene();
        this.scene.add(this.view);
        // The camera needs to be in the scene because we add the frame mask as
        // a child and the mask needs to be rendered.
        this.scene.add(this.camera);

        this.sceneEnvironment = new UpdateSceneEnvironment(this.renderer, DefaultLoadingManager);
        this.sceneEnvironment.update(this.scene, this.config.environmentAssetId);

        this.camera.position.set(0, 42, 120)
        sizeReady(this.root.canvas, () => {
            this.camera.aspect = this.root.canvas.clientWidth / this.root.canvas.clientHeight;

            this.nav.setup(this.config.savedViews[0],
                           this.room.walls,
                           this.renderer.domElement,
                           this.scene);
            this.nav.onUpdate.add(this.loop.update);
        });

        this.loop.update();
        this.loop.start();
    }

    render = () => {
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
    }

    resize = () => {
        this.framingHelper.update();
        this.loop.update();
    }

    showWallConfigChanged = () => {
        for(const conf of this.config.showWalls) {
            this.view.setWallViewVisible(conf.wallOrder, conf.show);
        }
        this.loop.update();
    };

    updateCurrentViewConfig = () => {
        const current = this.root.viewsPanel.current;
        current.camera = extractSavedViewCamera(this.camera);

        update(current.lighting, this.view.config.directionalLighting);
        assign(current.wallMaterial, this.view.config.wallMaterial);

        current.walls.length = this.view.walls.length;
        for(let i=0; i < current.walls.length; i++) {
            const wall = this.view.walls[i];
            let config = current.walls[i];
            if(config == null) {
                current.walls[i] = config = clone(showWallDefaults);

            }
            config.wallOrder = wall.model.wallOrder;
            config.show = wall.visible;
            config.island = wall.model.island;
        }

        // TODO: save the floor material
    };

    applyCurrentViewConfig = () => {
        const current = this.root.viewsPanel.current;
        applySavedViewCamera(current.camera, this.camera);

        update(this.config.room.directionalLighting, current.lighting);
        update(this.view.config.directionalLighting, current.lighting);

        assign(this.view.config.wallMaterial, current.wallMaterial);
        for(const wall of this.room.walls) {
            wall.material.tint = current.wallMaterial.tint;
        }

        update(this.config.showWalls, current.walls);
        for(const conf of current.walls) {
            this.view.setWallViewVisible(conf.wallOrder, conf.show);
        }

        // TODO: apply the floor material

        this.loop.update();
    };

    directionalLightingInput = () => {
        update(this.config.room.directionalLighting, this.root.stagingPanel.config.directionalLighting);
        update(this.view.config.directionalLighting, this.root.stagingPanel.config.directionalLighting);
        this.loop.update();
    }

    directionalLightingChange = () => {
        update(this.config.room.directionalLighting, this.root.stagingPanel.config.directionalLighting);
        update(this.view.config.directionalLighting, this.root.stagingPanel.config.directionalLighting);
        optimizeGlobalLightingView(this.room);
        this.loop.update();
    }

    updateWallColor = () => {
        assign(this.view.config.wallMaterial, this.root.stagingPanel.config.wallMaterial);
        for(const wall of this.room.walls) {
            wall.material.tint = this.root.stagingPanel.config.wallMaterial.tint;
        }
        this.loop.update();
    };

    updateRenderFrameVisibility = () => {
        const show = this.root.cameraPanel.showRenderFrame.checked;
        this.framingHelper.visible = show;
        this.loop.update();
    };

    updateCameraFocalLength = () => {
        const focalLength = Number(this.root.cameraPanel.focalLengthInput.value);
        this.framingHelper.vFov = focalLengthToVFov(focalLength);
        this.framingHelper.update();
        this.loop.update();
    };

    toggleRenderFrameVisibility = () => {
        const showRenderFrame = this.root.cameraPanel.showRenderFrame;
        showRenderFrame.checked = ! showRenderFrame.checked;
        this.framingHelper.visible = showRenderFrame.checked;
        this.loop.update();
    };

    showTemporaryWallColor = () => {
        const newColor = new Color(this.root.stagingPanel.config.wallMaterial.tint);
        for(const wall of this.view.walls) {
            for(const mesh of wall.meshes) {
                if(Array.isArray(mesh.material)) {
                    for(const mat of mesh.material) {
                        mat["color"].copy(newColor);
                    }
                } else {
                    mesh.material["color"].copy(newColor);
                }
            }
        }
        for(const fillers of this.view.fillersByWallOrder.values()) {
            for(const mesh of fillers) {
                if(Array.isArray(mesh.material)) {
                    for(const mat of mesh.material) {
                        mat["color"].copy(newColor);
                    }
                } else {
                    mesh.material["color"].copy(newColor);
                }
            }
        }

        this.loop.update();
    };


    async exportRenderFile() {
        const scene = new Scene();
        scene.userData = clone(this.scene.userData);
        scene.add(this.framingHelper.exportCamera());
        await new Promise<void>(resolve => {
            const model = this.room.clone(true);
            model.commit(true);
            const view = new RoomView(model, this.config.room);
            view.onReady.add(resolve);
            scene.add(view);
            // We preload all of the materials to help onReady resolve
            // correctly. TODO: formalize this somewhere.
            model.traverse((model) => {
                if(model instanceof Material) {
                    if(model.assetId || model.type) {
                        mfac.makefrom(model);
                    }
                }
            });
            view.updateFunctionBasedViews();
        });
        saveBuffer(await exportForRender(scene), 'render.glb');
    }

}
export default WalkInApp;


